import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { RouteHeader } from '../components/RouteHeader';
import { Sidebar } from '../components/Sidebar';
import type { VerticalLayoutProps } from '../type';

/**
 * The signed-in chrome: sidebar, navbar, routed content.
 *
 * Shared by both portals, but it makes **no** portal-specific decisions of its
 * own - the nav tree, brand mark, profile route and sign-out handler all arrive
 * as props. That is what keeps the mls and vendor shells identical in behaviour
 * while free to differ in content, without either app importing the other's.
 */
export const VerticalLayout = ({
  navigation,
  brand,
  user,
  onSignOut,
  profilePath,
  navbarSlot,
  titleSuffix = 'Admin Panel',
}: VerticalLayoutProps): ReactNode => {
  const { t } = useTranslation();

  return (
    <div className="flex h-screen overflow-hidden bg-ds-bg-page">
      {/*
      Bypass block (WCAG 2.4.1, Level A). The sidebar renders up to 43 rows across eleven
      sections, and they come before the content in DOM order on every screen — so without
      this a keyboard user tabs the whole nav again after each navigation just to reach the
      page. Visually hidden until focused, then pinned over the sidebar's top-left.

      It targets `#main-content` on the `<main>` below, which is focusable only as a target
      (`tabIndex={-1}`): without that, following the link moves the viewport but leaves focus
      on the link, and the next Tab goes straight back into the nav.
    */}
      <a
        href="#main-content"
        className="sr-only z-50 focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:rounded-lg focus:bg-ds-action-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white focus:outline-none focus:ring-2 focus:ring-ds-action-primary focus:ring-offset-2"
      >
        {t('shell.skipToContent')}
      </a>
      <Sidebar navigation={navigation} brand={brand} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Navbar user={user} onSignOut={onSignOut} profilePath={profilePath} slot={navbarSlot} />
        {/*
        `relative` pairs with `overflow-y-auto`: it makes this element the
        containing block for absolutely positioned descendants, so they scroll and
        clip with the content instead of resolving against the viewport.

        Without it a screen could put a SECOND scrollbar on the page. An absolutely
        positioned box is clipped by an `overflow: hidden` ancestor only when that
        ancestor is, or contains, its containing block — so an `absolute` descendant
        with no positioned ancestor escapes the shell's own `overflow-hidden`, gets
        laid out at its static position far down the scrolled content, and extends
        the DOCUMENT's scroll height. Tailwind's `sr-only` is exactly such a box, and
        it is the kind of thing that lands in a table cell without anyone thinking
        about position; the mls dashboard's "Live Data" column did precisely this and
        added ~1500px of phantom document scroll.
      */}
        {/* `tabIndex={-1}` makes this a focus target for the skip link above; see there. */}
        <main id="main-content" tabIndex={-1} className="relative flex-1 overflow-y-auto p-6">
          <RouteHeader titleSuffix={titleSuffix} />
          <Outlet />
        </main>
      </div>
    </div>
  );
};
